import React, { useState } from 'react';
import { StaffMember } from './availability/types';
import { useAvailabilityForm } from './availability/useAvailabilityForm';
import RecurringScheduleTab from './availability/RecurringScheduleTab';

interface EditAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffMembers: StaffMember[];
  initialStaffId: string | null;
  onSave?: () => void;
}

const EditAvailabilityModal: React.FC<EditAvailabilityModalProps> = ({
  isOpen,
  onClose,
  staffMembers,
  initialStaffId,
  onSave,
}) => {
  const [saveError, setSaveError] = useState<string | null>(null);

  // Use the first staff member if no initial ID provided
  const defaultStaffId = initialStaffId || (staffMembers.length > 0 ? staffMembers[0].id : '');

  const {
    staffId,
    recurring,
    isLoading,
    isSaving,
    error,
    actions,
  } = useAvailabilityForm(defaultStaffId);

  // Handle save
  const handleSave = async () => {
    setSaveError(null);
    const result = await actions.saveRecurring();

    if (result.success) {
      if (onSave) {
        onSave();
      }
      onClose();
    } else {
      setSaveError(result.error || 'Failed to save changes');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-800">Edit Availability</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        {/* Subtitle */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Set the recurring weekly schedule for when staff members are available for bookings.
            Use the "Block Time" button on the calendar to block specific times.
          </p>
        </div>

        {/* Content - Recurring Schedule Only */}
        <div className="px-6">
          <RecurringScheduleTab
            schedule={recurring}
            staffMembers={staffMembers}
            selectedStaffId={staffId}
            onStaffChange={actions.changeStaff}
            onToggleDay={actions.toggleRecurringDay}
            onAddSlot={actions.addRecurringSlot}
            onRemoveSlot={actions.removeRecurringSlot}
            onUpdateSlot={actions.updateRecurringSlot}
            isLoading={isLoading}
          />
        </div>

        {/* Error Display */}
        {(saveError || error) && (
          <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{saveError || error}</p>
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="px-4 py-2 bg-[#3A5E63] text-white rounded-md hover:bg-[#2d484c] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Saving...
              </>
            ) : (
              'Done'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAvailabilityModal;
